import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
} from "recharts";

import {
  Card,
  Row,
  Col,
  Typography,
  DatePicker,
  Select,
  Form,
  Table,
  Progress,
  Space
} from "antd";
import ViewEmpCalendar from "./ViewEmpCalendar";
import { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import LeaveCalendar from "../employee/LeaveCalendar";

const { Title, Text } = Typography;
const { Option } = Select;

export default function ViewOneEmployeeRecord({ employee, isProfile }) {
  const currentMonth = dayjs().month() + 1;
  const currentYear = dayjs().year();
  const { authData, backUrl } = useContext(AuthContext);
  const [leaves_taken_breakdown, setLeavesTakenBreakdown] = useState({});

  console.log("Employee : ", employee);

  // ✅ Normalize: support both direct employee object and nested employee.employee
  const emp = employee?.employee ?? employee;

  useEffect(() => {
    if (employee) {
      setLeavesTakenBreakdown(employee.leaves_taken_breakdown || {});
    }
  }, [employee]);

  const [graphData, setGraphData] = useState({
    month: currentMonth,
    year: currentYear,
  });
  const [totalLeaves, setTotalLeaves] = useState([]);

  // ✅ Fetch total leaves with month and year as query params
  const fetchTotalLeaves = async () => {
    try {
      const params = { employee: emp?.id };
      if (graphData.month) params.month = graphData.month;
      if (graphData.year) params.year = graphData.year;

      const res = await axios.get(`${backUrl}/api/hr/leaves/`, {
        params,
        headers: {
          Authorization: `Bearer ${authData?.access}`,
        },
      });
      setTotalLeaves(res.data.results || []);
    } catch (err) {
      console.error("Error fetching total leaves:", err);
    }
  };

  const [compoffBalance, setCompoffBalance] = useState(0);

  const fetchCompoffBalance = async () => {
    if (!emp?.id) return;
    try {
      const res = await axios.get(`${backUrl}/api/hr/compoffs/?employee=${emp.id}&status=Available`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      const items = res.data.results || res.data || [];
      const total = Array.isArray(items) ? items.reduce((sum, item) => sum + (parseFloat(item.days_available) || 1.0), 0) : 0;
      setCompoffBalance(total);
    } catch (err) {
      console.error("Error fetching compoff balance:", err);
    }
  };

  // ✅ Re-fetch whenever employee, month, or year changes
  useEffect(() => {
    if (emp?.id) {
      fetchTotalLeaves();
      fetchCompoffBalance();
    }
  }, [employee, graphData.month, graphData.year]);

  // ✅ Leaves already filtered by backend
  const filteredLeaves = totalLeaves;

  console.log("Employee", employee);

  // ✅ Compute leave counts by total days
  const leaveTypeCounts = filteredLeaves.reduce((acc, leave) => {
    const type = leave.leave_type || "Unknown";
    const days = Number(leave.leave_days_count) || 0;
    acc[type] = (acc[type] || 0) + days;
    return acc;
  }, {});

  const pieData = Object.entries(leaveTypeCounts).map(([name, value]) => ({
    name,
    value,
  }));

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28EFF"];

  const handleMonthChange = (date) => {
    setGraphData((prev) => ({
      ...prev,
      month: date ? date.month() + 1 : "",
    }));
  };

  const handleYearChange = (value) => {
    setGraphData((prev) => ({ ...prev, year: value }));
  };

  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const leaveColumns = [
    { title: "Leave Type", dataIndex: "leave_type", key: "leave_type" },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 300,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {text}
        </div>
      ),
    },
    { title: "Duration Type", dataIndex: "duration_type", key: "duration_type" },
    { title: "Leave Days", dataIndex: "leave_days_count", key: "leave_days_count" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span
          style={{
            color:
              status === "Approved"
                ? "green"
                : status === "Rejected"
                  ? "red"
                  : "orange",
            fontWeight: 500,
          }}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Document",
      dataIndex: "leave_document",
      key: "leave_document",
      render: (file) =>
        file ? (
          <a href={file} target="_blank" rel="noopener noreferrer">
            View
          </a>
        ) : (
          "-"
        ),
    },
  ];

  // ✅ Leave balance — safe for both direct and nested employee objects
  const slUsed = emp?.leave_balance?.SL?.used ?? emp?.taken_sick_leave ?? 0;
  const slTotal = emp?.leave_balance?.SL?.total ?? emp?.actual_sick_leave ?? 0;

  const clUsed = emp?.leave_balance?.CL?.used ?? emp?.taken_casual_leave ?? 0;
  const clTotal = emp?.leave_balance?.CL?.total ?? emp?.actual_casual_leave ?? 0;

  return (
    <Card bordered={false} style={{ margin: "5px", width: "100%" }}>
      {/* ✅ Summary */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-around",
          alignItems: "center",
          gap: "10px",
          marginBottom: "20px",
          textAlign: "center",
        }}
      >
        <Space
          direction="vertical"
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around",
          }}
          size="small"
        >
          {/* SL */}
          <div>
            <Text strong>SL TAKEN: </Text>
            <Text>
              {slUsed} / {slTotal}
            </Text>
            <Progress
              percent={slTotal ? (slUsed / slTotal) * 100 : 0}
              strokeColor="red"
              showInfo={false}
              size={[200, 6]}
              style={{ marginTop: 4 }}
            />
          </div>

          {/* CL */}
          <div>
            <Text strong>CL TAKEN: </Text>
            <Text>
              {clUsed} / {clTotal}
            </Text>
            <Progress
              percent={clTotal ? (clUsed / clTotal) * 100 : 0}
              strokeColor="red"
              showInfo={false}
              size={[200, 6]}
              style={{ marginTop: 4 }}
            />
          </div>

          {/* COMP-OFF BALANCE */}
          <div style={{ textAlign: "center" }}>
            <Text strong>COMP-OFF BALANCE: </Text>
            <Text style={{ color: "#0e497b", fontWeight: "bold", fontSize: "15px" }}>
              {leaves_taken_breakdown?.compoff_balance !== undefined ? leaves_taken_breakdown.compoff_balance : compoffBalance} Days
            </Text>
          </div>
        </Space>

        {leaves_taken_breakdown?.lop_leave > 0 && (
          <h5 style={{ color: "red" }}>
            NLB: {leaves_taken_breakdown?.lop_leave}
          </h5>
        )}
      </div>

      {/* ✅ Month / Year Filters */}
      <Form
        layout="inline"
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "10px",
          marginBottom: "20px",
        }}
      >
        <Form.Item label="Month">
          <DatePicker
            picker="month"
            onChange={handleMonthChange}
            allowClear
            value={graphData.month ? dayjs().month(graphData.month - 1) : null}
            style={{ width: 160 }}
          />
        </Form.Item>

        <Form.Item label="Year">
          <Select
            style={{ width: 140 }}
            placeholder="Select Year"
            onChange={handleYearChange}
            allowClear
            value={graphData.year}
          >
            {years.map((yr) => (
              <Option key={yr} value={yr}>
                {yr}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>

      {/* ✅ Leave Table */}
      <Table
        columns={leaveColumns}
        dataSource={filteredLeaves.map((item) => ({
          ...item,
          key: item.id,
        }))}
        pagination={{ pageSize: 5, responsive: true }}
        scroll={{ x: "max-content" }}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  backgroundColor: "#0e497b",
                  color: "white",
                  textAlign: "center",
                }}
              >
                {props.children}
              </th>
            ),
          },
        }}
      />

      {/* ✅ Month-Year Info */}
      <Row
        gutter={[16, 16]}
        justify="space-between"
        align="middle"
        style={{ marginTop: 20 }}
      >
        <Col xs={24} md={12}>
          <Text type="secondary">
            Showing leaves for: {graphData.month || "--"}/{graphData.year || "--"}
          </Text>
        </Col>
      </Row>

      {!isProfile && (
        <>
        </>
      )}
    </Card>
  );
}